import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormBuilderService } from 'services/form-builder/form-builder.service';
import { FormInputType } from 'services/models/form-builder/enum/form-input-type.enum';
import { FormGroupModel } from 'services/models/form-builder/form-group.model';
import { FormItemModel } from 'services/models/form-builder/form-item.model';
import { FormSubmissionAnswerModel } from 'services/models/form-builder/form-submission-answer-model';
import { FormSubmissionModel } from 'services/models/form-builder/form-submission.model';
import { FormModel } from 'services/models/form-builder/form.model';

@Component({
  selector: 'app-form-submission',
  templateUrl: './form-submission.component.html',
  styleUrls: ['./form-submission.component.scss'],
})
export class FormSubmissionComponent implements OnInit, OnChanges {
  @Input() formData: FormModel;
  @Input() readonly: boolean = false;
  @Input() isSubmitting: boolean;
  @Input() currentStep: number = 0;
  @Input() submission: FormSubmissionModel;

  @Output() onPrev = new EventEmitter();
  @Output() onCancel = new EventEmitter();
  @Output() onSubmit = new EventEmitter();

  public isLoading = false;
  public formItems: FormItemModel[] = [];
  public group: FormGroupModel;
  public formAnswers: FormSubmissionAnswerModel[] = [];
  public formStructure: FormModel;
  public initialValues: { [key: string]: any } = {};

  constructor(private _formBuilderService: FormBuilderService) {}

  ngOnInit(): void {
    this._loadData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.currentStep && !changes.currentStep.firstChange) {
      this.group = this.getGroup(this.currentStep);
      this.formItems = this.getOrderedItems(this.currentStep);
      this.initialValues = this.getInitialValues(this.currentStep);
    }
  }

  public onSubmitForm(items: FormSubmissionAnswerModel[]): void {
    const updatedItems = items.map(item => ({
      ...item,
      formSubmissionId: this.submission.id,
    }));

    this.onSubmit.emit({
      items: updatedItems,
      isLastGroup: this.isLastGroup,
    });
  }

  private async _loadData() {
    try {
      this.isLoading = true;

      const [formAnswers, formStructure] = await Promise.all([
        this._formBuilderService.getAnswersById(this.submission.id).toPromise(),
        this._formBuilderService.getStructure(this.formData.id).toPromise(),
      ]);

      this.formAnswers = formAnswers;
      this.formStructure = formStructure;

      this.group = this.getGroup(this.currentStep);
      this.formItems = this.getOrderedItems(this.currentStep);
      this.initialValues = this.getInitialValues(this.currentStep);
    } catch (error) {
    } finally {
      this.isLoading = false;
    }
  }

  private getOrderedItems(step: number): any[] {
    return (
      this.formStructure?.groups?.[step]?.itens
        ?.slice()
        .sort((a, b) => (a.order || 0) - (b.order || 0)) || []
    );
  }

  private getGroup(step: number): FormGroupModel {
    return this.formStructure?.groups?.[step];
  }

  private getInitialValues(step: number): { [key: string]: any } {
    const group = this.formStructure?.groups?.[step];

    if (!group) return {};

    const values: { [key: string]: any } = {};

    group.itens.forEach(item => {
      const answer = this.formAnswers.find(a => a.formItemId === item.id);

      if (answer) {
        values[item.id] = this.isGridItem(item)
          ? this.buildGridInitialValue(item, answer.formItemOptionIds || [])
          : answer.value;
      }
    });

    return values;
  }

  private buildGridInitialValue(item: FormItemModel, optionIds: string[]) {
    const value: { [key: string]: string | string[] } = {};

    (item.options || []).forEach(option => {
      if (!optionIds.includes(option.id)) {
        return;
      }

      const rowLabel = (option.label || '').trim();
      const columnLabel = (option.legend || '').trim();
      if (!rowLabel || !columnLabel) {
        return;
      }

      if (item.inputType === FormInputType.RADIO_OPTION_GRID.value) {
        value[rowLabel] = columnLabel;
        return;
      }

      const currentValue = Array.isArray(value[rowLabel]) ? (value[rowLabel] as string[]) : [];
      value[rowLabel] = [...currentValue, columnLabel];
    });

    return value;
  }

  private isGridItem(item: FormItemModel): boolean {
    return [
      FormInputType.RADIO_OPTION_GRID.value,
      FormInputType.RADIO_OPTION_MULTISELECT_GRID.value,
    ].includes(item.inputType);
  }

  public get isLastGroup(): boolean {
    return this.currentStep === (this.formStructure?.groups?.length || 0) - 1;
  }
}
